/**
 * マニュアルのコンテンツ定義
 * サイドバーのナビゲーションとMarkdownファイルのマッピング
 */
import { brandConfig } from '../../config/brands';

export interface ManualPage {
  slug: string;
  titleKey: string; // glossaryのキーまたはプレーンテキスト
  title: string;    // デフォルト表示名
  parentSlug?: string; // 親ページのslug（子ページの場合に指定）
  adminOnly?: boolean; // 管理者限定機能のページ
}

export interface ManualSection {
  id: string;
  contentDir: string; // Markdownファイルのディレクトリ名
  title: string;
  pages: ManualPage[];
}

export const manualSections: ManualSection[] = [
  {
    id: 'onboarding',
    contentDir: 'getting-started',
    title: 'はじめよう',
    pages: [
      { slug: 'onboarding', titleKey: '', title: 'アカウント登録からスタートまで' },
    ],
  },
  {
    id: 'image',
    contentDir: 'getting-started',
    title: '静止画生成',
    pages: [
      { slug: 'image-generation', titleKey: '', title: '静止画を生成する' },
      { slug: 'attribute-mode', titleKey: 'attributeMode', title: '属性選択モード' },
      { slug: 'prompt-mode', titleKey: '', title: 'プロンプト入力モード' },
      { slug: 'image-editing', titleKey: '', title: '静止画を編集する' },
    ],
  },
  {
    id: 'video',
    contentDir: 'getting-started',
    title: '動画生成',
    pages: [
      { slug: 'video-generation', titleKey: '', title: '動画を生成する' },
      { slug: 'scenario-editing', titleKey: 'scenarioSettings', title: 'シナリオ生成設定' },
      { slug: 'scenario-scenes', titleKey: '', title: 'シーンの編集・素材生成' },
      { slug: 'storyboard', titleKey: '', title: 'ストーリーボード（絵コンテ）', parentSlug: 'scenario-scenes' },
      { slug: 'audio', titleKey: '', title: 'BGM・ナレーション', parentSlug: 'scenario-scenes' },
      { slug: 'express-mode', titleKey: '', title: '特急モード' },
      { slug: 'video-prompt-mode', titleKey: '', title: 'プロンプト入力モード' },
      { slug: 'video-export', titleKey: '', title: '動画の生成・エクスポート' },
    ],
  },
  {
    id: 'actors',
    contentDir: 'getting-started',
    title: '{{aiActors}}',
    pages: [
      { slug: 'ai-actors', titleKey: '', title: '{{aiActors}}を使う' },
      { slug: 'exclusive-actors', titleKey: '', title: '専属{{aiActors}}' },
    ],
  },
  {
    id: 'management',
    contentDir: 'getting-started',
    title: '管理機能',
    pages: [
      { slug: 'product-data', titleKey: '', title: '{{products}}を管理する' },
      { slug: 'amazon-integration', titleKey: '', title: 'Amazon連携', parentSlug: 'product-data' },
      { slug: 'manual-products', titleKey: '', title: 'マニュアル商品登録', parentSlug: 'product-data' },
      { slug: 'compliance', titleKey: '', title: 'コンプライアンスチェック（薬機法・景表法）' },
      { slug: 'brand-management', titleKey: '', title: '{{brandManagement}}' },
      { slug: 'brand-cuts', titleKey: '', title: 'ブランドカット・サウンドロゴ', parentSlug: 'brand-management' },
      { slug: 'logo-management', titleKey: '', title: 'ロゴ管理' },
      { slug: 'tag-management', titleKey: '', title: '{{tagManagement}}' },
      { slug: 'organization', titleKey: '', title: '{{organization}}と{{members}}の管理', adminOnly: true },
      { slug: 'profile', titleKey: '', title: 'マイページ' },
      { slug: 'api-connections', titleKey: '', title: 'API接続・連携', adminOnly: true },
      { slug: 'amazon-seller-api', titleKey: '', title: 'Amazon Seller Partner API', parentSlug: 'api-connections', adminOnly: true },
      { slug: 'amazon-ads-api', titleKey: '', title: 'Amazon Advertising API', parentSlug: 'api-connections', adminOnly: true },
      { slug: 'google-ads', titleKey: '', title: 'Google広告連携', parentSlug: 'api-connections', adminOnly: true },
      // Canva 連携を出さないブランド (features.canvaIntegration=false) では記事ごと隠す
      ...(brandConfig.features.canvaIntegration
        ? [{ slug: 'canva-integration', titleKey: '', title: 'Canva連携', parentSlug: 'api-connections' }]
        : []),
    ],
  },
  {
    id: 'use-cases',
    contentDir: 'use-cases',
    title: '活用ガイド',
    pages: [
      { slug: 'ec-product-images', titleKey: '', title: 'EC商品画像を作る' },
      { slug: 'banner-ads', titleKey: '', title: 'バナー広告を作る' },
      { slug: 'video-ads', titleKey: '', title: '動画広告を作る' },
      { slug: 'organizing-materials', titleKey: '', title: '素材の整理（タグと商品で管理）' },
      { slug: 'team-workflow', titleKey: '', title: 'チームで作業する' },
    ],
  },
  {
    id: 'billing',
    contentDir: 'billing',
    title: '料金・お支払い',
    pages: [
      { slug: 'plans-and-payment', titleKey: '', title: 'ポイント制について' },
      { slug: 'purchase', titleKey: '', title: 'ポイントの都度購入', adminOnly: true },
      { slug: 'subscription', titleKey: '', title: 'サブスクリプションプラン', adminOnly: true },
    ],
  },
  {
    id: 'faq',
    contentDir: '',
    title: 'よくある質問',
    pages: [
      { slug: 'faq', titleKey: '', title: 'よくある質問' },
    ],
  },
];

/**
 * Markdownファイルを動的にインポート
 * Viteのimport.meta.globで全mdファイルを取得
 */
const markdownModules = import.meta.glob('./getting-started/*.md', { query: '?raw', import: 'default' });
const useCaseModules = import.meta.glob('./use-cases/*.md', { query: '?raw', import: 'default' });
const billingModules = import.meta.glob('./billing/*.md', { query: '?raw', import: 'default' });
const faqModule = import.meta.glob('./faq.md', { query: '?raw', import: 'default' });

const allModules: Record<string, () => Promise<unknown>> = {
  ...markdownModules,
  ...useCaseModules,
  ...billingModules,
  ...faqModule,
};

/**
 * 本文中のブランド機能フラグによる出し分け。
 *
 * Markdown 内の `<!-- feature:フラグ名 -->` 〜 `<!-- /feature:フラグ名 -->` で
 * 囲んだ範囲を、brandConfig.features のフラグが falsy のブランドでは本文ごと除去する
 * (truthy ならマーカーだけ除去)。`feature:!フラグ名` で条件を反転でき、
 * フラグ off のブランド向けの代替文面を書ける。Markdown パース前のテキスト処理なので、
 * テーブル行や箇条書きの 1 行をマーカー行で挟んでも表・リストは分断されない。
 */
export function applyFeatureGates(
  markdown: string,
  features: Record<string, boolean | undefined> = brandConfig.features as Record<string, boolean | undefined>,
): string {
  return markdown.replace(
    /<!--\s*feature:(!?)(\w+)\s*-->\n?([\s\S]*?)<!--\s*\/feature:\1\2\s*-->\n?/g,
    (_match, negate: string, flag: string, body: string) => {
      const enabled = Boolean(features[flag]);
      return (negate ? !enabled : enabled) ? body : '';
    },
  );
}

export async function loadMarkdown(contentDir: string, slug: string): Promise<string> {
  const path = !contentDir || slug === 'faq'
    ? `./faq.md`
    : `./${contentDir}/${slug}.md`;

  const loader = allModules[path];
  if (!loader) {
    return `# ページが見つかりません\n\nリクエストされたページ「${contentDir}/${slug}」は存在しません。`;
  }

  return applyFeatureGates((await loader()) as string);
}

/**
 * セクションIDからcontentDirを解決
 */
export function findSection(sectionId: string): ManualSection | undefined {
  return manualSections.find(s => s.id === sectionId);
}

export function findPage(sectionId: string, slug: string): ManualPage | undefined {
  const sec = findSection(sectionId);
  return sec?.pages.find(p => p.slug === slug);
}

/**
 * 全ページをフラットなリストとして取得（前後ナビゲーション用）
 */
export interface FlatPage {
  sectionId: string;
  slug: string;
  title: string;
}

export function getFlatPages(): FlatPage[] {
  const pages: FlatPage[] = [];
  for (const s of manualSections) {
    for (const p of s.pages) {
      pages.push({ sectionId: s.id, slug: p.slug, title: p.title });
    }
  }
  return pages;
}

/**
 * 現在のページの前後ページを取得
 */
export function getAdjacentPages(sectionId: string, slug: string): { prev: FlatPage | null; next: FlatPage | null } {
  const flat = getFlatPages();
  const idx = flat.findIndex(p => p.sectionId === sectionId && p.slug === slug);
  if (idx === -1) return { prev: null, next: null };
  return {
    prev: idx > 0 ? flat[idx - 1] : null,
    next: idx < flat.length - 1 ? flat[idx + 1] : null,
  };
}
